import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle, PartyPopper, Send } from "lucide-react";
import { toast } from "sonner";
import { API, axios, apiErr } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const MAX_POLLS = 12;

const turnaroundLabel = (hours) => (hours <= 48 ? "48-hour" : `${Math.round(hours / 24)}-day`);

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  useSEO({ title: "Payment | BrandToBuilt", path: "/payment/success", noindex: true });
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [product, setProduct] = useState("");
  const [turnaroundHours, setTurnaroundHours] = useState(48);
  const [deadlineLabel, setDeadlineLabel] = useState("");
  const [form, setForm] = useState({ name: "", business: "", phone: "", email: "", services: "", details: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("failed"); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      setProduct(data.product_name || "");
      setTurnaroundHours(data.turnaround_hours || 48);
      if (data.payment_status === "paid") { setState("paid"); return; }
      if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
    } catch { /* keep trying */ }
    if (attempt >= MAX_POLLS) { setState("timeout"); return; }
    setTimeout(() => poll(attempt + 1), 2000);
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business) { toast.error("Please add your name and business name."); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/launch-intake`, { ...form, session_id: sessionId });
      setDeadlineLabel(data.deadline_label || "");
      setSubmitted(true);
      toast.success(`Received! Your launch clock has started.`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="payment-success">
      <div className="max-w-2xl mx-auto px-5 md:px-8">
        {state === "checking" && (
          <div className="text-center py-20" data-testid="payment-checking">
            <Loader2 size={40} className="animate-spin text-[#ccff00] mx-auto mb-6" />
            <p className="font-display font-bold text-2xl">Confirming your payment…</p>
            <p className="text-zinc-500 mt-2">One moment — this only takes a few seconds.</p>
          </div>
        )}

        {state === "failed" && (
          <div className="text-center py-20" data-testid="payment-failed">
            <XCircle size={44} className="text-[#ff0055] mx-auto mb-6" />
            <p className="font-display font-black text-3xl mb-3">Payment not completed</p>
            <p className="text-zinc-400 mb-8">Something went wrong or the payment was cancelled. You haven't been charged.</p>
            <button onClick={() => navigate("/#pricing")} className="rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-xs px-7 py-4">Back to pricing</button>
          </div>
        )}

        {state === "timeout" && (
          <div className="text-center py-20" data-testid="payment-timeout">
            <Loader2 size={40} className="text-zinc-500 mx-auto mb-6" />
            <p className="font-display font-bold text-2xl mb-3">Still processing…</p>
            <p className="text-zinc-400 mb-8">Your payment is taking a little longer than usual. If you were charged, you'll get a confirmation email shortly — no need to pay again.</p>
            <button onClick={() => navigate("/")} className="rounded-full border border-white/20 text-white font-bold uppercase tracking-widest text-xs px-7 py-4">Back to site</button>
          </div>
        )}

        {state === "paid" && !submitted && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-10">
              <CheckCircle2 size={48} className="text-[#ccff00] mx-auto mb-5" />
              <p className="text-[#ccff00] text-xs font-semibold uppercase tracking-widest mb-3">Payment confirmed{product ? ` · ${product}` : ""}</p>
              <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl leading-tight mb-4">Your {turnaroundLabel(turnaroundHours)} launch starts now.</h1>
              <p className="text-zinc-400">Tell us about your business below and the clock begins. The more you share, the faster we build.</p>
            </div>

            <form onSubmit={submit} className="bg-[#121212] border border-white/10 rounded-2xl p-6 md:p-8 space-y-4" data-testid="launch-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name *" value={form.name} onChange={set("name")} placeholder="Sarah Jones" testid="launch-name" />
                <Field label="Business name *" value={form.business} onChange={set("business")} placeholder="Sparkle Cleaning" testid="launch-business" />
                <Field label="Phone" value={form.phone} onChange={set("phone")} placeholder="07…" testid="launch-phone" />
                <Field label="Email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" testid="launch-email" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">What services do you offer?</label>
                <input value={form.services} onChange={set("services")} placeholder="e.g. domestic cleaning, end-of-tenancy, ironing" className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none" data-testid="launch-services" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Anything else? (colours you like, businesses you admire, location)</label>
                <textarea value={form.details} onChange={set("details")} rows={4} placeholder="Tell us anything that helps us nail your brand…" className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none resize-none" data-testid="launch-details" />
              </div>
              <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 hover:scale-[1.01] transition-transform disabled:opacity-60" data-testid="launch-submit">
                {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <><Send size={18} /> Start the clock</>}
              </button>
            </form>
          </motion.div>
        )}

        {submitted && (
          <div className="text-center py-16" data-testid="launch-done">
            <PartyPopper size={48} className="text-[#ccff00] mx-auto mb-6" />
            <h1 className="font-display font-black text-4xl md:text-5xl mb-4">You're all set, {form.name.split(" ")[0]}!</h1>
            <p className="text-zinc-400 max-w-md mx-auto mb-8" data-testid="launch-deadline">
              We've got everything we need to start building {form.business}. {deadlineLabel ? `Your business will be live by ${deadlineLabel} (UK time).` : "Watch your inbox — we'll be in touch shortly."}
            </p>
            <button onClick={() => navigate("/")} className="rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-xs px-7 py-4">Back to home</button>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, type = "text", value, onChange, placeholder, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">{label}</label>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none" data-testid={testid} />
  </div>
);

export default PaymentSuccess;
